import React, {useMemo} from "react";
import {MediaCategory} from "../../../../types/Media";
import {useTranslation} from "react-i18next";
import {useGlobalContext} from "../../../../contexts/globalContext";
import cx from "classnames";
import styles from "./EditCategoryModalContent.module.scss";
import Button from "../../../../components/Button";
import Input from "../../../../components/Input";
import Select from "../../../../components/Select";
import {useUpdateData} from "../../../../hooks/useUpdateData";
import * as yup from "yup";
import {treeOptions} from "../../../../helpers/treeOptions";

type EditCategoryModalContentProps = {
    category: MediaCategory,
    categories: MediaCategory[]
    onSubmit: () => void
}

const EditCategoryModalContent: React.FC<EditCategoryModalContentProps> = (
    {
        category,
        categories,
        onSubmit,
    }
) => {
    const { t } = useTranslation(['settings']);
    const editCategorySchema = useMemo(() => {
        const pos_parents = categories.filter(
            (cat) => cat.parent_id !== category.id && cat.id !== category.id
        ).map((c) => c.id+'')

        pos_parents.push('')

        return ({
            name: yup.string().required().min(3),
            parent_id: yup.string().oneOf(pos_parents),
        })
    },[categories, category])

    const {
        element,
        changeProp,
        isValid,
        submit
    } = useUpdateData<MediaCategory>('mediaCategories', category, category.id, editCategorySchema)

    const { showToast,closeModal } = useGlobalContext();

    const handleSubmit = () => {
        submit().then((res) => {
            if (res.status === 200) {
                showToast("top-right", "success", t("categories.edit_modal.success"));
                onSubmit();
                closeModal();
            } else {
                showToast("top-right", "error", t("categories.edit_modal.success"));
            }
        }).catch(() => {
            showToast("top-right", "error", t("categories.edit_modal.success"));
        });
    };

    const selectOptions = useMemo(() => {
        return treeOptions<MediaCategory>(
            categories.filter(
                (cat) => cat.parent_id !== category.id && cat.id !== category.id
            ),
            null,
            'name',
            'id',
            'parent_id',
            'id',
            0
        )
    }, [categories, category])

    return (
        <div className={cx(styles["modal-wrapper"])}>
            <Input
                property={"name"}
                type="text"
                placeholder={t("categories.labels.name")}
                value={element.name}
                onChange={(property,value) => changeProp({name: value})}
                classes={"w-100 mx-4 my-3"}
            />
            <Select
                options={selectOptions}
                onSelect={(property, value) => changeProp({parent_id: value})}
                property={"parentCategory"}
                value={element.parent_id+''}
                placeholder={t("categories.labels.parent")}
                classes={"w-100 mx-4 my-3"}
            />
            <Button
                hierarchy="primary"
                text={t('categories.edit_modal.action')}
                fn={() => handleSubmit()}
                disabled={!isValid}
                classes={'w-100 mx-4 my-2'}
            />
            <Button
                hierarchy="secondary"
                text={t('common.abort')}
                fn={() => closeModal()}
                classes={'w-100 mx-4 my-2'}
            />
        </div>
    );
};


export default EditCategoryModalContent;
